import React from "react";
import Modal from './Modal';
import { speak, voicesForLanguage } from '../speak';

// Pick a text-to-speech voice by listening to it, rather than by guessing from
// a name in a dropdown. Names like "Google español" or "Mónica" tell you almost
// nothing about how a voice actually sounds, and the list differs on every
// browser and OS — so the only useful way to choose is to hear them.
//
// Clicking a voice previews it and saves it immediately. That does mean a save
// per click while you're comparing, which is a fair price for never leaving
// someone with a choice that silently didn't stick.
export default class VoicePicker extends React.Component {
  constructor(props) {
    super(props);
    this.state = { selected: props.selected || '' };
  }

  // Saves on click rather than on a confirm button. Some languages offer a
  // couple of dozen voices, and anything that needs scrolling past all of them
  // to take effect is something a fair number of people will simply never do —
  // they'd pick a voice, close the modal, and find nothing had changed.
  choose(voice) {
    const name = voice ? voice.name : '';
    this.setState({ selected: name });
    this.props.onSelect(name);
    if(voice) {
      speak(this.props.sampleText, voice);
    }
  }

  done() {
    window.speechSynthesis && window.speechSynthesis.cancel();
    this.modal && this.modal.hide();
  }

  // The chosen row says so in words as well as colour. A tinted background alone
  // is easy to miss in a long list, and now that clicking saves immediately this
  // label is the only confirmation that anything happened.
  renderChoiceMarker(selected) {
    if(selected) {
      return (
        <span className="text-nowrap ml-2">
          <i className="fa fa-check-circle mr-1" aria-hidden="true"></i>
          Selected
        </span>
      );
    }
    return <span className="text-nowrap ml-2 text-muted"><i className="fa fa-volume-up" aria-hidden="true"></i></span>;
  }

  renderVoice(voice) {
    const selected = this.state.selected === voice.name;
    return (
      <button
        type="button"
        key={voice.name + voice.lang}
        aria-pressed={selected}
        className={'list-group-item list-group-item-action d-flex justify-content-between align-items-center' + (selected ? ' active font-weight-bold' : '')}
        onClick={() => this.choose(voice)}
      >
        <span>
          {voice.name}
          <br />
          <small className={selected ? 'font-weight-normal' : 'text-muted'}>
            {voice.lang}
            {voice.localService === false && ' · needs a connection'}
          </small>
        </span>
        {this.renderChoiceMarker(selected)}
      </button>
    );
  }

  renderBody() {
    const voices = voicesForLanguage(this.props.voices, this.props.twoLetterLanguageCode);

    if(!voices.length) {
      return (
        <p className="mb-0">
          Your browser isn't offering any {this.props.languageEnglishName} voices. These come from
          your browser and device rather than from us, so the list varies — Google Chrome usually
          has the most, and on phones you may need to install a voice in your system settings.
        </p>
      );
    }

    return (
      <>
        <p>
          These voices come from your browser and device, not from us, so what's available differs
          by browser and computer. Click one to hear it — your choice is saved straight away.
        </p>
        {!this.props.sampleText && (
          <p className="text-muted"><small>No sentence to preview with yet — pick a voice and try it in the game.</small></p>
        )}
        <div className="list-group">
          {/* Letting the game pick has to stay reachable: it's the default, and
              it's the only option that survives moving to another device, where
              a hand-picked voice may simply not exist. */}
          <button
            type="button"
            aria-pressed={this.state.selected === ''}
            className={'list-group-item list-group-item-action d-flex justify-content-between align-items-center' + (this.state.selected === '' ? ' active font-weight-bold' : '')}
            onClick={() => this.choose(null)}
          >
            <span>
              Automatic
              <br />
              <small className={this.state.selected === '' ? 'font-weight-normal' : 'text-muted'}>
                Use whichever {this.props.languageEnglishName} voice this device offers
              </small>
            </span>
            {this.state.selected === '' && this.renderChoiceMarker(true)}
          </button>
          {voices.map((voice) => this.renderVoice(voice))}
        </div>
      </>
    );
  }

  render() {
    // One button, and it only closes — the choice is already saved. Anything
    // labelled like a commit step would imply the click that saved it didn't.
    const footer = (
      <button type="button" className="btn btn-primary" onClick={() => this.done()}>Done</button>
    );

    return (
      <Modal
        ref={(el) => this.modal = el}
        title="Choose a voice"
        scrollable={true}
        footer={footer}
        onHidden={this.props.onHidden}
      >
        {this.renderBody()}
      </Modal>
    );
  }
}
